import React, { ReactNode } from 'react';
import { AppBar, Toolbar, Typography, Box, Container, Tabs, Tab, Chip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import MarketStatusBar from './MarketStatusBar';
import { useMarketStore } from '../store/marketStore';
import { useTradeStore } from '../store/tradeStore';
import { formatMoney } from '../utils/format';
import { NAV_ITEMS } from '../utils/constants';
import { useWebSocketSync } from '../hooks/useWebSocketSync';

/**
 * 顶栏布局组件
 * 包含：导航 + 大盘状态条 + 账户资产 + WebSocket 实时同步
 */
export default function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { startPolling: startMarketPolling } = useMarketStore();
  const { startPolling: startTradePolling, account } = useTradeStore();

  // Initialize polling as fallback
  React.useEffect(() => {
    startMarketPolling();
    startTradePolling();
  }, [startMarketPolling, startTradePolling]);

  // Initialize WebSocket real-time sync (falls back to polling if unavailable)
  useWebSocketSync();

  const currentTab = NAV_ITEMS.findIndex((item) => {
    if (item.path === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.path);
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* 顶部导航栏 */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'primary.main' }}>
        <Toolbar variant="dense">
          <Typography variant="h6" sx={{ fontWeight: 700, mr: 4 }}>
            股票量化交易
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <Tabs
              value={currentTab >= 0 ? currentTab : 0}
              onChange={(_e, v) => navigate(NAV_ITEMS[v].path)}
              textColor="inherit"
              indicatorColor="secondary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ '& .MuiTab-root': { minWidth: 'auto', px: 1.5, fontSize: 13 } }}
            >
              {NAV_ITEMS.map((item) => (
                <Tab key={item.path} label={item.label} sx={{ minHeight: 48 }} />
              ))}
            </Tabs>
          </Box>
          {/* 账户资产 */}
          {account && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                size="small"
                label={`总资产 ${formatMoney(account.totalAssets)}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`可用 ${formatMoney(account.balance)}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }}
              />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* 大盘状态条 */}
      <MarketStatusBar />

      {/* 页面内容 */}
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {children}
      </Container>
    </Box>
  );
}
